import { Link } from "gatsby";
import styled, { createGlobalStyle, css } from "styled-components";

const breakpoints = {
    sm: "320px",
    md: "425px",
    lg: "768px",
    xl: "1024px",
    "2xl": "1440px",
};

type BreakpointsType = typeof breakpoints;
type BreakpointName = keyof BreakpointsType;

type BreakpointDirection = "and-up" | "and-down";

function getMediaQuery(
    breakpoint: BreakpointName,
    direction: BreakpointDirection,
): `(width ${">" | "<="} ${BreakpointsType[BreakpointName]})` {
    const unequals = direction === "and-up" ? ">" : "<=";
    const breakpointVal = breakpoints[breakpoint];
    return `(width ${unequals} ${breakpointVal})`;
}

/**
 * Get the media rule for a breakpoint and direction combination.
 * @param breakpoint The breakpoint name
 * @param direction The direction of the breakpoint, either 'and-up' or 'and-down'
 * @returns The media rule
 */
export function reactiveRule(
    breakpoint: BreakpointName,
    direction: BreakpointDirection,
) {
    const mediaQuery = getMediaQuery(breakpoint, direction);
    return `@media ${mediaQuery}`;
}

export const XsAndSm = reactiveRule("md", "and-down");

const colors = {
    black: `#000000`,
    white: `#ffffff`,
    darkBlue1: `#06002a`,
    darkBlue2: `#080038`,
    darkGray: `#25203a`,
    gray: `#cacaca`,
    lightGray: `#b6b6b6`,
    accent: `#fcdc10`,
};

export const theme = {
    breakpoints,
    colors,
};

const StickyFixer = css`
    position: fixed;
`;

export const GlobalStyles = createGlobalStyle`
    html {
        margin: 0;
        padding: 0;
    }

    body {
        background: ${({ theme }) => theme.colors.darkBlue1};
        margin: 0;
        padding: 0;
        color: ${({ theme }) => theme.colors.white};
        font-family: Poppins, sans-serif;
        font-weight: 400;
        font-size: 28px;
        overflow-x: hidden;
        ${StickyFixer}
    }

    a {
        color: inherit;
        text-decoration: none;
    }
`;

export const AccentLink = styled(Link)`
    color: ${({ theme }) => theme.colors.accent};
`;

type Themetype = typeof theme;

declare module "styled-components" {
    // eslint-disable-next-line
    export interface DefaultTheme extends Themetype {}
}
